import type {
  AssetView,
  CanvasDocumentV3 as CanvasDocument,
  CanvasEdge,
  CanvasNodeV3 as CanvasNode
} from "@seedance/contracts";
import { type ReferenceMention } from "@seedance/workflow";
import { createCanvasConnection } from "../connection-actions.js";
import {
  buildEditorReferenceMap,
  updateSeedanceSettings
} from "../seedance-editor.js";
import { applyCanvasReferenceCommandV3, getPromptContent, type PromptContent } from "@seedance/contracts";
import { replacePromptRange, type PromptRange } from "../prompts/prompt-edit-operations.js";

type MediaNode = Extract<
  CanvasNode,
  { kind: "image" | "video" | "audio" }
>;

export interface ComposerReferenceResult {
  document: CanvasDocument;
  nodeId: string;
  edgeId: string;
  mention: ReferenceMention;
  createdNode: boolean;
}

export interface AddComposerReferenceInput {
  document: CanvasDocument;
  seedanceNodeId: string;
  asset: AssetView;
  canvasOffer?: {nodeId:string;edgeId:string|null};
  anchor: { x: number; y: number };
  createId(): string;
}

export interface AddComposerReferenceWithPromptInput extends AddComposerReferenceInput {
  prompt: PromptContent;
  query: PromptRange;
}

function isMediaNode(node: CanvasNode): node is MediaNode {
  return node.kind === "image" || node.kind === "video" || node.kind === "audio";
}

function findSeedanceNode(document: CanvasDocument, nodeId: string): Extract<CanvasNode, { kind: "seedance" }> {
  const node = document.nodes.find((candidate) => candidate.id === nodeId);
  if (node?.kind !== "seedance") throw new Error("Seedance node not found");
  return node;
}

function makeMediaNode(asset: AssetView, id: string, anchor: { x: number; y: number }): MediaNode {
  return {
    id,
    kind: asset.kind,
    assetId: asset.id,
    position: { ...anchor },
    size: asset.kind === "audio" ? { width: 300, height: 96 } : { width: 320, height: 200 },
    collapsed: false
  };
}

function findExistingMediaNode(document: CanvasDocument, asset: AssetView): MediaNode | undefined {
  return document.nodes
    .filter((node): node is MediaNode => isMediaNode(node) && node.kind === asset.kind && node.assetId === asset.id)
    .sort((left, right) => left.id.localeCompare(right.id))[0];
}

function connectionFor(
  document: CanvasDocument,
  source: MediaNode,
  target: Extract<CanvasNode, { kind: "seedance" }>,
  createId: () => string
): CanvasEdge {
  const existing = document.edges.find(
    (edge) => edge.sourceNodeId === source.id && edge.targetNodeId === target.id
  );
  if (existing !== undefined) return existing;
  const created = createCanvasConnection({
    id: createId(),
    source,
    target,
    existingEdges: document.edges
  });
  if (created === undefined) throw new Error("Unable to create media reference edge");
  return created;
}

function findMention(
  document: CanvasDocument,
  seedanceNodeId: string,
  edgeId: string
): ReferenceMention {
  const mention = buildEditorReferenceMap(document, seedanceNodeId).find(
    (reference) => reference.edgeId === edgeId
  )?.mention;
  if (mention === undefined) throw new Error("Media reference mention not found");
  return mention;
}

export function addComposerReference({
  document,
  seedanceNodeId,
  asset,
  anchor,
  createId,
  canvasOffer
}: AddComposerReferenceInput): ComposerReferenceResult {
  const target = findSeedanceNode(document, seedanceNodeId);
  const offered = canvasOffer ? document.nodes.find(node => node.id === canvasOffer.nodeId) : undefined;
  if (canvasOffer && (!offered || !isMediaNode(offered) || offered.assetId !== asset.id || offered.kind !== asset.kind)) throw new Error("所选画布素材已更新或移除，请重新选择。");
  const existing = canvasOffer ? offered as MediaNode : findExistingMediaNode(document, asset);
  const source = existing ?? makeMediaNode(asset, createId(), anchor);
  const currentEdges = document.edges.filter(edge => edge.sourceNodeId === source.id && edge.targetNodeId === target.id);
  if (canvasOffer && (canvasOffer.edgeId === null ? currentEdges.length !== 0 : !currentEdges.some(edge => edge.id === canvasOffer.edgeId))) throw new Error("所选画布连线已更新或移除，请重新选择。");
  const withNode = existing === undefined
    ? { ...document, nodes: [...document.nodes, source] }
    : document;
  const edge = canvasOffer?.edgeId ? currentEdges.find(edge => edge.id === canvasOffer.edgeId)! : connectionFor(withNode, source, target, createId);
  const next = withNode.edges.some((candidate) => candidate.id === edge.id)
    ? withNode
    : { ...withNode, edges: [...withNode.edges, edge] };
  return {
    document: next,
    nodeId: source.id,
    edgeId: edge.id,
    mention: findMention(next, seedanceNodeId, edge.id),
    createdNode: existing === undefined
  };
}

/** Apply only to the exact current source content and graph identities. */
export function addComposerReferenceWithPrompt({prompt, query, ...input}: AddComposerReferenceWithPromptInput): ComposerReferenceResult {
  const source = {nodeId:input.seedanceNodeId,field:"prompt" as const,inputEdgeId:null};
  if (JSON.stringify(getPromptContent(input.document, source)) !== JSON.stringify(prompt)) throw new Error("提示词已更新，请重新选择素材。");
  const result = addComposerReference(input);
  const selected = prompt.segments[query.start.segmentIndex];
  const isRebind = selected?.type === "reference" && query.start.offset === 0 && query.end.segmentIndex === query.start.segmentIndex && query.end.offset === 1;
  if (isRebind && selected.kind !== input.asset.kind) throw new Error("请选择相同媒体类型的素材。");
  const slotId = isRebind ? selected.slotId : input.createId();
  // Semantic labels only: private displayName must never become portable text.
  const label = isRebind ? selected.label : ({image:"图片参考",video:"视频参考",audio:"音频参考"} as const)[input.asset.kind];
  const content = isRebind ? prompt : replacePromptRange(prompt, query, [{type:"reference",slotId,kind:input.asset.kind,label}]);
  const changed = updateSeedanceSettings(result.document, input.seedanceNodeId, {prompt:content});
  return {...result,document:applyCanvasReferenceCommandV3(changed,{type:"bind_reference",targetNodeId:input.seedanceNodeId,source,slotId,mediaEdgeId:result.edgeId})};
}
export function removeComposerReference(document: CanvasDocument, seedanceNodeId: string, edgeId: string): CanvasDocument {
  if (!document.edges.some(edge => edge.id === edgeId && edge.targetNodeId === seedanceNodeId)) throw new Error("Media reference edge not found");
  return applyCanvasReferenceCommandV3(document,{type:"delete_edges",edgeIds:[edgeId]});
}
